/**
 * Custom hook for managing chat session lifecycle
 * Handles session persistence, recovery, and cleanup
 */

import { useEffect } from 'react';
import { useChatStore } from '@/stores/chatStore';
import { useToast } from '@/hooks/use-toast';

export const useChatSession = () => {
  const { toast } = useToast();
  const { 
    currentSession, 
    sessionId, 
    isLoading, 
    initializeChat, 
    restoreSession, 
    createNewSession,
    cleanup 
  } = useChatStore();

  // Initialize session on mount - only once!
  useEffect(() => {
    let mounted = true;
    
    const initSession = async () => {
      // Only initialize if mounted and no session exists and not already loading
      if (!mounted || currentSession || isLoading) {
        return;
      }
      
      try {
        await initializeChat();
      } catch (error) {
        if (!mounted) return;
        
        console.error('Failed to initialize chat session:', error);
        toast({
          title: "Session Error",
          description: "Failed to initialize chat session. Creating a new session...",
          variant: "destructive"
        });
        
        // Fallback to creating a new session
        try {
          await createNewSession();
        } catch (createError) {
          if (!mounted) return;
          
          console.error('Failed to create new session:', createError);
          toast({
            title: "Connection Error", 
            description: "Unable to establish chat session. Please refresh the page.",
            variant: "destructive"
          });
        }
      }
    };

    initSession();
    
    return () => {
      mounted = false;
    };
  }, []); // Empty dependency array - only run once on mount!

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Don't cleanup on unmount in production - let session persist
      // cleanup();
    };
  }, []);

  return {
    sessionId: currentSession?.id || sessionId,
    isSessionReady: !!currentSession,
    isLoading,
    restoreSession,
    createNewSession,
    cleanup
  };
};